import { Typography } from "@hellocarbo/lasagne";
import { ascendingSort } from "@hellocarbo/lasagne/utils";
import BarChart, { ECOption } from "@ui/BarChart";
import ChartTooltip from "@ui/ChartTooltip";
import { TooltipComponentOption } from "echarts/components";
import { TFunction } from "i18next";
import { renderToString } from "react-dom/server";
import { useTranslation } from "react-i18next";

import { Subsidiary } from "@/types";
import { formatCarbon, getCarbonDisplayUnit } from "@/utils/carbon";

import colors from "../../../../colors";

function optionFactory(
  data: Array<Subsidiary>,
  total: number,
  t: TFunction
): ECOption {
  // Get the best unit
  const subsidiariesUnit = getCarbonDisplayUnit(
    data.map((dataItem) => dataItem.carbon)
  );

  // Convert all the carbon values to the provided unit
  // and store the localized carbon string.
  const formattedData = data.map((dataItem) => {
    const { formattedCarbon, convertedCarbon } = formatCarbon(
      t,
      dataItem.carbon,
      subsidiariesUnit
    );
    return {
      ...dataItem,
      carbon: convertedCarbon,
      carbonToDisplay: formattedCarbon,
    };
  }) satisfies Array<Subsidiary & { carbonToDisplay: string }>;

  // Convert the total
  const { convertedCarbon: convertedTotal } = formatCarbon(
    t,
    total,
    subsidiariesUnit
  );

  return {
    textStyle: {
      fontFamily: "Poppins",
      color: colors["typography"],
    }, // Global text styles
    tooltip: {
      borderWidth: 0,
      padding: 0,
      extraCssText: "border-radius: 8px;",
      formatter: ({
        name,
        value,
        color,
      }: {
        name: string;
        value: number;
        color: string;
      }) =>
        renderToString(
          <ChartTooltip
            seriesName={name}
            valueToDisplay={
              formattedData.find(
                (subsidiary) => subsidiary.company_name === name
              )!.carbonToDisplay
            }
            unit={t(`common:unit.${subsidiariesUnit}`)}
            color={color}
            iconClassName="size-2xs"
            extraInfo={
              <div className="flex items-center justify-between">
                <Typography step="-2">
                  {t("dashboard:consolidation.shareOfEmissions")}
                </Typography>
                <Typography step="-2">
                  {t("formatter:percentage", {
                    value: value / convertedTotal,
                  })}
                </Typography>
              </div>
            }
          />
        ),
    } as TooltipComponentOption,
    grid: {
      left: "0%",
      bottom: "0%",
      top: "0%",
      right: "6%",
      containLabel: true,
    }, // Configure the grid
    xAxis: {
      type: "value",
      axisLine: { show: false }, // Hide x lines
      axisTick: { show: false }, // Hide x small ticks
      axisLabel: {
        formatter: (value) =>
          `${t("formatter:number", { value })} ${t(`common:unit.${subsidiariesUnit}`)}`,
        fontSize: 10,
        color: colors["grayscale"].DEFAULT,
      },
      splitLine: { show: false }, // Hide vertical split lines
    },
    yAxis: {
      type: "category",
      data: data.map((subsidiary) => subsidiary.company_name),
      axisLine: { show: false }, // Hide y lines
      axisTick: { show: false }, // Hide small ticks
      axisLabel: {
        margin: 12,
        width: 120,
        overflow: "truncate",
        fontWeight: 600,
        fontSize: 14,
      }, // Format y labels
    },
    series: [
      {
        type: "bar",
        data: formattedData.map((subsidiary) => subsidiary.carbon),
        barMaxWidth: 32,
        itemStyle: {
          color: colors["primary"].light,
          borderRadius: 6,
        },
        label: {
          show: true,
          position: "right",
          formatter: ({ value }) => {
            return t("formatter:percentage", {
              value: Number(value) / convertedTotal,
            });
          },
          fontSize: 14,
        }, // Percent of total displayed to the right of the bar
        emphasis: {
          focus: "series",
          itemStyle: {
            color: colors["primary"].DEFAULT,
            shadowBlur: 15,
            shadowColor: "rgba(9, 4, 74, 0.12)",
            shadowOffsetX: 0,
            shadowOffsetY: 3,
          },
        }, // Focus state
      },
    ],
  };
}

function ReviewBySubsidiaries({
  subsidiaries,
  total,
}: {
  subsidiaries: Array<Subsidiary>;
  total: number;
}) {
  const { t } = useTranslation(["common", "formatter", "dashboard"]);

  const option = optionFactory(
    subsidiaries.sort((a, b) => ascendingSort(a.carbon, b.carbon)),
    total,
    t
  );

  return (
    <div className="mr-md h-full">
      <BarChart option={option} />
    </div>
  );
}

export default ReviewBySubsidiaries;
